import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { venueService } from "services/venueService";
import { RootState } from "store";
import { clearPrice } from "store/apps/price";
import { CheckoutPromotion } from "types/checkout";
import { PromotionType } from "types/promotion";
import useCheckoutContact from "./useCheckoutContact";
import useCheckoutPromotion from "./useCheckoutPromotion";
import { errorAlert } from "utils/alertHandler";
import { useTranslation } from "react-i18next";
import useCheckoutVoucher from "./useCheckoutVoucher";

const { REACT_APP_DEFAULT_VENUE_ID } = process.env;

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
};

function validatePhoneNumber(phoneNumber: string) {
  // Remove any non-digit characters from the input
  var cleanPhoneNumber = phoneNumber.replace(/\D/g, "");

  // Check if the cleaned phone number has exactly 8 digits
  if (cleanPhoneNumber.length === 8) {
    return true;
  } else {
    return false;
  }
}

export default function useCheckout() {
  const { startTime, endTime, totalPrice, totalHours, discountedPrice } = useSelector((state: RootState) => state.price);

  const settings: any = useSelector((state: RootState) => state.settings);

  const [isCreatingBooking, setIsCreatingBooking] = useState<boolean>(false);

  const { points, pointUsed, pointInfo, userAccountPointBalance, isRedeemPointLoading, setPoints, _onRedeemPoint } = useCheckoutPromotion();

  const { user, email, phone, instagram_id, first_name, last_name, remarks, _onEmailChange, _onPhoneChange, _onRemarksChange } = useCheckoutContact();

  const { voucher, _onSelectVoucher, _onClearVoucher } = useCheckoutVoucher();

  const [isConfirmTerms, setIsConfirmTerms] = useState<boolean>(false);

  const [isOpenTerms, setIsOpenTerms] = useState<boolean>(false);

  let isProfileAvailableToCheckout = false;

  if (first_name !== undefined && last_name !== undefined && email !== undefined && phone !== undefined && instagram_id !== undefined) {
    if (first_name !== null && last_name !== null && email !== null && phone !== null && instagram_id !== null) {
      if (first_name !== "" && last_name !== "" && email !== "" && phone !== "" && instagram_id !== "") {
        isProfileAvailableToCheckout = true;
      }
    }
  }
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const _onCreate = async () => {
    try {
      setIsCreatingBooking(true);

      if (!startTime || !endTime) throw new Error("Please Select Time.");

      /** Temp stop checking email */
      // if (!validateEmail(String(email))) throw new Error(t("checkout_error_invalid_email"));

      if (!validatePhoneNumber(String(phone))) throw new Error(t("checkout_error_invalid_phone"));

      if (totalHours > settings.settings.max_booking_hours) {
        throw new Error(`${t("booking_time_too_long")} ${settings.settings.max_booking_hours} ${t("hours")} `);
      }

      if (totalHours < settings.settings.min_booking_hours) {
        throw new Error(`${t("booking_time_too_short")} ${settings.settings.min_booking_hours} ${t("hours")} `);
      }

      if (!isConfirmTerms) throw new Error(`${t("checkout_checkbox_error")} ${t("terms")} ${","} ${t("personal_data_collection")} ${t("and")} ${t("disclaimer")}`);

      const promotion: CheckoutPromotion = {
        promotion_type_id: PromotionType.POINT,
        points: pointUsed,
      };

      const result = await venueService.createVenueBooking(
        Number(REACT_APP_DEFAULT_VENUE_ID),
        startTime,
        endTime,
        email,
        phone,
        remarks,
        pointUsed,
        voucher?.["voucher"]
          ? {
              vocuher_id: voucher?.["voucher"]?.["voucher_id"],
              quantity: voucher?.["appliedCount"],
            }
          : null
      );

      if (!result) throw new Error("Cannot Create Booking.");

      const data = {
        venue_booking_id: result.venue_booking_id,
        startTime: startTime,
        endTime: endTime,
        totalHours: totalHours,
        totalPrice: totalPrice,
        pointUsed: pointUsed,
        email: email,
        phone: String(phone),
        remarks: remarks,
        points: pointUsed,
        vouchers: voucher,
      };

      navigate("/pay-done", {
        state: data,
      });

      dispatch(clearPrice());

      setIsCreatingBooking(false);
    } catch (error: any) {
      setIsCreatingBooking(false);
      errorAlert(t(error?.message));
    }
  };

  const _onConfirmTerms = () => {
    setIsConfirmTerms((prev) => !prev);
  };

  const _isOpenTerms = () => {
    setIsOpenTerms((prev) => !prev);
  };

  return {
    user,
    totalPrice,
    totalHours,
    startTime,
    endTime,
    isCreatingBooking,
    points,
    pointUsed,
    pointInfo,
    userAccountPointBalance,
    isRedeemPointLoading,
    email,
    phone,
    instagram_id,
    remarks,
    isConfirmTerms,
    isOpenTerms,
    voucher,
    discountedPrice,
    isProfileAvailableToCheckout,
    _onEmailChange,
    _onPhoneChange,
    _onRemarksChange,
    setPoints,
    _onRedeemPoint,
    _onCreate,
    _onConfirmTerms,
    _isOpenTerms,
    _onSelectVoucher,
    _onClearVoucher,
  };
}
